/**
 * NameGeneratorUI — Part5「ネームジェネレータ」モードの回答UI。
 *
 * 所属部署内の同僚を最大 maxSelect 名までプルダウンで選択する（ONA具体化の置き換え）。
 * 選択結果（同僚ID配列）が §03つながり構造レベルBの相談エッジのデータ源になる。
 * 実名表示は会社単位設定（レポート側）の話で、ここでは入力（誰を選んだか）を収集する。
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface Colleague {
  id: string;
  name: string;
}

interface NameGeneratorUIProps {
  questionText: string;
  colleagues: Colleague[];
  selectedIds: string[];
  maxSelect?: number;
  onChange: (ids: string[]) => void;
  onNext: () => void;
  onBack: () => void;
  canGoBack: boolean;
}

export const NameGeneratorUI: React.FC<NameGeneratorUIProps> = ({
  questionText,
  colleagues,
  selectedIds,
  maxSelect = 3,
  onChange,
  onNext,
  onBack,
  canGoBack,
}) => {
  const { t } = useTranslation();
  const slots = Array.from({ length: maxSelect }, (_, i) => selectedIds[i] ?? '');

  const setSlot = (i: number, id: string) => {
    const next = [...slots];
    next[i] = id;
    onChange(next.filter(Boolean));
  };

  return (
    <>
      <h2 className="gb74-q-text">{questionText}</h2>
      <p style={{ fontSize: 13, color: '#5a6470', margin: '0 0 14px' }}>
        所属部署の中で、仕事のことでよく相談する人を最大{maxSelect}名まで選んでください。
        （いなければ空欄のままでかまいません）
      </p>

      {colleagues.length === 0 ? (
        <p style={{ fontSize: 13, color: '#9aa3ad' }}>選択できる同僚がいません。</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {slots.map((val, i) => {
            const chosenElsewhere = new Set(slots.filter((_, j) => j !== i).filter(Boolean));
            const options = colleagues.filter((c) => !chosenElsewhere.has(c.id));
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: '#EAF3DE',
                    color: '#3B6D11',
                    fontWeight: 700,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </span>
                <select
                  value={val}
                  onChange={(e) => setSlot(i, e.target.value)}
                  style={{
                    flex: 1,
                    padding: '12px 10px',
                    fontSize: 15,
                    border: '1.5px solid #C0DD97',
                    borderRadius: 8,
                    background: '#fff',
                    color: '#33414f',
                  }}
                >
                  <option value="">― 選択 ―</option>
                  {options.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={onNext}
        style={{
          width: '100%',
          marginTop: 18,
          padding: '14px',
          fontSize: 16,
          fontWeight: 700,
          color: '#fff',
          background: '#3B6D11',
          border: 'none',
          borderRadius: 10,
          cursor: 'pointer',
        }}
      >
        {t('questionnaire.next', '次へ')}
      </button>

      {canGoBack && (
        <p className="gb74-back">
          <button type="button" onClick={onBack}>
            {t('questionnaire.previousQuestion')}
          </button>
        </p>
      )}
    </>
  );
};
